/**
 * Web Push on this device.
 *
 * `public/service-worker.js` receives the pushes; this registers it. It is
 * registered at the root scope so it can open any page a notification names,
 * which is also why nginx serves it from `/` and `no-cache`.
 */

export const SERVICE_WORKER_URL = "/service-worker.js";

// Resolves to the registration, or null where the browser has no service
// workers (an old browser, or a non-secure origin): that means no push on this
// device, not an error.
export async function registerServiceWorker(nav = typeof navigator !== "undefined" ? navigator : {}) {
  if (!nav || !nav.serviceWorker) return null;
  try {
    return await nav.serviceWorker.register(SERVICE_WORKER_URL, { scope: "/" });
  } catch (e) {
    return null;
  }
}

// What this browser can do about a push, WITHOUT asking anything: "unsupported"
// (no Notification, no PushManager or no service worker — an old browser, a
// non-secure origin, or iOS outside the Home Screen app), "blocked" (the person
// refused the prompt, and only the browser's own settings can undo that), or
// "ok". Reading `Notification.permission` never prompts; only
// `requestPermission()` does, and that is called from a tap alone.
export function pushSupport(win = typeof window !== "undefined" ? window : {}) {
  if (!win || !("Notification" in win) || !win.PushManager
      || !win.navigator || !win.navigator.serviceWorker) {
    return "unsupported";
  }
  return win.Notification.permission === "denied" ? "blocked" : "ok";
}

// The VAPID public key arrives base64url; `pushManager.subscribe` wants bytes.
export function urlBase64ToUint8Array(value) {
  const padded = (value + "=".repeat((4 - (value.length % 4)) % 4))
    .replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

// A name a person recognises their device by, read from the user agent the
// browser sent when it subscribed. A guess, and labelled as nothing more.
export function deviceName(userAgent) {
  const ua = userAgent || "";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /Firefox\/|FxiOS\//.test(ua) ? "Firefox"
    : /Chrome\/|CriOS\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : null;
  const system = /iPhone/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android"
    : /Windows/.test(ua) ? "Windows"
    : /Macintosh|Mac OS X/.test(ua) ? "macOS"
    : /Linux|X11/.test(ua) ? "Linux" : null;
  if (browser && system) return `${browser} on ${system}`;
  return browser || system || "A browser";
}
